import React, { lazy, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Routes, Route, useLocation } from "react-router-dom";

import { LINKS } from "../../constants";
import { VerifiedRoute, AuthenticatedRoute } from "components/routes";
import WebsiteLayout from "layouts/WebsiteLayout";
import AppLayout from "layouts/AppLayout";
import eventsService from "services/events";

const NotFound = lazy(() => import("pages/NotFound"));
const HomePage = lazy(() => import("pages/HomePage"));
const PricingPage = lazy(() => import("pages/PricingPage"));
const UseCasesPage = lazy(() => import("pages/UseCasesPage"));
const FAQPage = lazy(() => import("pages/FAQPage"));
const TOUPage = lazy(() => import("pages/TOUPage"));
const AUPPage = lazy(() => import("pages/AUPPage"));
const ToolsPage = lazy(() => import("pages/ToolsPage"));
const AccountPage = lazy(() => import("pages/AccountPage"));
const StatsPage = lazy(() => import("pages/StatsPage"));
const ProjectsPage = lazy(() => import("pages/ProjectsPage"));
const ProjectPage = lazy(() => import("pages/ProjectPage"));
const SignInPage = lazy(() => import("pages/SignInPage"));
const SignUpPage = lazy(() => import("pages/SignUpPage"));
const MobileLoginPage = lazy(() => import("pages/MobileLoginPage"));
const UnverifiedPage = lazy(() => import("pages/UnverifiedPage"));
const VerifyEmailPage = lazy(() => import("pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("pages/ForgotPasswordPage"));
const AuthRedirect = lazy(() => import("pages/AuthRedirect"));
const OAuth2RedirectPage = lazy(() => import("pages/OAuth2RedirectPage"));
const ForgotPasswordEmailSentPage = lazy(() =>
  import("pages/ForgotPasswordEmailSentPage")
);
const ChangePasswordPage = lazy(() => import("pages/ChangePasswordPage"));
const ChangePasswordSuccessPage = lazy(() =>
  import("pages/ChangePasswordSuccessPage")
);
const PlanPage = lazy(() => import("pages/PlanPage"));
const CalibrationsPage = lazy(() => import("pages/CalibrationsPage"));
const CreateCalibrationsPage = lazy(() =>
  import("pages/CreateCalibrationPage")
);
const VoicesPage = lazy(() => import("pages/VoicesPage"));
const ChooseVoicesPage = lazy(() => import("pages/ChooseVoicesPage"));
const VoiceTalentsPage = lazy(() => import("pages/VoiceTalentsPage"));
const CreateRecordingPage = lazy(() => import("pages/CreateRecordingPage"));
const LoginFromRedirectPage = lazy(() => import("pages/LoginFromRedirectPage"));
const HelpVideoPage = lazy(() => import("pages/HelpVideoPage"));
const WelcomeWizardPage = lazy(() => import("pages/WelcomeWizardPage"));
const AudioSettingsPage = lazy(() => import("pages/AudioSettingsPage"));

const LayoutPicker = ({ isAuthenticated, isWebsitePages }) => {
  if (!isAuthenticated && isWebsitePages) {
    return <WebsiteLayout />;
  } else {
    return <AppLayout />;
  }
};

LayoutPicker.propTypes = {
  isAuthenticated: PropTypes.bool,
  isWebsitePages: PropTypes.bool,
};

const Router = () => {
  const location = useLocation();
  const path = location.pathname;

  const { isAuthenticated } = useSelector((state) => state.auth);
  const isWebsitePages = [
    LINKS.home,
    LINKS.faq,
    LINKS.pricing,
    LINKS.useCases,
    LINKS.termsOfUse,
    LINKS.acceptableUsePolicy,
  ].includes(path);

  useEffect(() => {
    let props = { logged_user: isAuthenticated, from: location?.state?.from };
    eventsService.viewPage(path, props);
  }, [isAuthenticated, location]);

  return (
    <>
      <Routes>
        <Route
          element={
            <LayoutPicker
              isAuthenticated={isAuthenticated}
              isWebsitePages={isWebsitePages}
            />
          }
        >
          <Route
            path={LINKS.home}
            element={
              <VerifiedRoute isAbleForGuests={true}>
                <>
                  {isAuthenticated && <ProjectsPage />}
                  {!isAuthenticated && <HomePage />}
                </>
              </VerifiedRoute>
            }
          />
          <Route path={LINKS.useCases} element={<UseCasesPage />} />
          <Route path={LINKS.faq} element={<FAQPage />} />
          <Route path={LINKS.termsOfUse} element={<TOUPage />} />
          <Route path={LINKS.acceptableUsePolicy} element={<AUPPage />} />
          <Route path={LINKS.pricing} element={<PricingPage />} />
          <Route path={LINKS.login} element={<SignInPage />} />
          <Route path={LINKS.signup} element={<SignUpPage />} />
          <Route path={LINKS.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={LINKS.mobileLogin} element={<MobileLoginPage />} />
          <Route
            path={`${LINKS.loginFromRedirect}/:csrfToken`}
            element={<LoginFromRedirectPage />}
          />
          <Route
            path={`${LINKS.oauth2}/:provider`}
            element={<OAuth2RedirectPage />}
          />
          <Route path={LINKS.loginRedirect} element={<AuthRedirect />} />
          <Route
            path={LINKS.forgotPasswordEmailSent}
            element={<ForgotPasswordEmailSentPage />}
          />
          <Route
            path="/change-password/:changePasswordId"
            element={<ChangePasswordPage />}
          />
          <Route
            path={LINKS.changePasswordSuccess}
            element={<ChangePasswordSuccessPage />}
          />
          <Route
            path={"/verify/email/:userId/:token"}
            element={<VerifyEmailPage />}
          />
          <Route
            path={LINKS.unverified}
            element={
              <AuthenticatedRoute>
                <UnverifiedPage />
              </AuthenticatedRoute>
            }
          />
          <Route
            path={LINKS.account}
            element={
              <VerifiedRoute>
                <AccountPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.statistics}
            element={
              <VerifiedRoute>
                <StatsPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.plan}
            element={
              <VerifiedRoute>
                <PlanPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.calibrations}
            element={
              <VerifiedRoute>
                <CalibrationsPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.createCalibration}
            element={
              <VerifiedRoute>
                <CreateCalibrationsPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.voiceTalents}
            element={
              <VerifiedRoute>
                <VoiceTalentsPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.auditionVoices}
            element={
              <VerifiedRoute>
                <VoicesPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.chooseVoices}
            element={
              <VerifiedRoute>
                <ChooseVoicesPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.createRecording}
            element={
              <VerifiedRoute>
                <CreateRecordingPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.helpVideo}
            element={
              <VerifiedRoute>
                <HelpVideoPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.welcomeWizard}
            element={
              <VerifiedRoute>
                <WelcomeWizardPage />
              </VerifiedRoute>
            }
          />
          <Route
            path={LINKS.audioSettings}
            element={
              <VerifiedRoute>
                <AudioSettingsPage />
              </VerifiedRoute>
            }
          />
          {/* FIXME for dev purposes only */}
          <Route path={"tools"} element={<ToolsPage />} />
          <Route
            path="/:ownerName/:projectSlug"
            element={
              <VerifiedRoute>
                <ProjectPage />
              </VerifiedRoute>
            }
          />
          {/* <VerifiedRoute
        isAbleForGuests={true}
        exact
        path={LINKS.home}
        component={HomePage}
      />
      <VerifiedRoute
        isAbleForGuests={true}
        exact
        path={LINKS.auditionVoices}
        component={AuditionVoicesPage}
      />
      <Route exact path={LINKS.termsOfUse} component={ToUPage} />
      <Route exact path={LINKS.acceptableUsePolicy} component={AupPage} />
      <Route exact path={LINKS.login} component={LoginForm} />
      <Route exact path={LINKS.forgotPassword} component={ForgotPasswordForm} />
      <Route exact path={LINKS.useCases} component={UseCasesPage} />
      <VerifiedRoute
        exact
        path={LINKS.plan}
        component={SubscriptionPage}
      />
      <VerifiedRoute
        exact
        path={LINKS.calibration}
        component={CalbrationPage}
      />
      <Route
        exact
        path="/change-password/:changePasswordId"
        component={ChangePasswordForm}
      />
      <Route
        exact
        path="/verify/email/:userId/:token/"
        component={VerifyEmailPage}
      />
      <Route exact path={LINKS.faq} component={FAQPage} />
      // TODO subscribed page
      <VerifiedRoute
        exact
        path={LINKS.subscribed}
        component={SubscriptionSuccessPage}
      />
      <VerifiedRoute
        exact
        path="/:ownerName/:projectSlug"
        component={ProjectPage}
      />
      <Route exact path={LINKS.signup} component={RegistrationPage} />
      <AdminRoute exact path={LINKS.users} component={UsersPage} /> */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
};
export default Router;
